import React, { useMemo, useState } from 'react';
import { Check, ChevronRight, Plus, Trash2, XCircle } from 'lucide-react';
import * as service from '../../../services/bauordnungService';
import type { SchreibKontext } from '../../../services/bauordnungService';
import { abschnittVon, abschnittBefunde, kapitelDesDokuments, regelnDesKapitels } from '../bauordnungLogic';
import { kapitelNachTeilen, type Dokumentart } from '../kapitel';
import type { BauordnungBundle, Regel } from '../types';
import type { ProjektMerkmale } from '../../planungsphase/types';

/**
 * Der Arbeitsreiter: Kapitel waehlen, Regelungen schreiben, bestaetigen.
 *
 * Darstellend — jede Entscheidung liegt in `bauordnungLogic.ts` oder im
 * Dienst. Diese Datei ordnet an und ruft auf.
 */

interface Props {
    bundle: BauordnungBundle;
    dokument: Dokumentart;
    merkmale: ProjektMerkmale;
    ctx: SchreibKontext;
    gesperrt: boolean;
    schreiben: <T>(vorgang: () => Promise<T>) => Promise<T | null>;
}

export const KapitelReiter: React.FC<Props> = ({ bundle, dokument, merkmale, ctx, gesperrt, schreiben }) => {
    const gezogen = useMemo(
        () => kapitelDesDokuments(bundle, dokument, merkmale),
        [bundle, dokument, merkmale],
    );
    const befunde = useMemo(
        () => new Map(abschnittBefunde(bundle, dokument, merkmale).map(b => [b.kapitel, b])),
        [bundle, dokument, merkmale],
    );
    const bloecke = useMemo(() => kapitelNachTeilen(gezogen), [gezogen]);

    const [offen, setOffen] = useState<string>(gezogen[0]?.definition.id ?? '');
    const aktiv = gezogen.find(g => g.definition.id === offen) ?? gezogen[0];
    const kennung = aktiv?.definition.id ?? '';

    const abschnitt = abschnittVon(bundle.abschnitte, kennung);
    const regeln = regelnDesKapitels(bundle.regeln, kennung);
    const ordnung = bundle.ordnung;

    /** Lokaler Entwurf je Regel — sonst springt der Textcursor bei jedem Neuladen. */
    const [entwurf, setEntwurf] = useState<Record<string, string>>({});
    const [einleitung, setEinleitung] = useState<string | null>(null);

    if (!ordnung || !aktiv) return null;

    const speichereEinleitung = async () => {
        if (einleitung === null) return;
        await schreiben(() => service.setzeAbschnitt(ordnung, kennung, { einleitung }, ctx));
        setEinleitung(null);
    };

    const neueRegel = () =>
        schreiben(() => service.addRegel(ordnung, kennung, { sortierung: regeln.length }, ctx));

    const speichereRegel = async (r: Regel) => {
        const text = entwurf[r.id];
        if (text === undefined || text === r.wortlaut) return;
        await schreiben(() => service.updateRegel(r.id, { wortlaut: text }, ctx));
        setEntwurf(e => { const n = { ...e }; delete n[r.id]; return n; });
    };

    return (
        <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-5">
            {/* ─── Kapitelliste ─────────────────────────────────────────── */}
            <div className="space-y-4 lg:max-h-[70vh] lg:overflow-y-auto lg:pr-1">
                {bloecke.map(block => (
                    <div key={block.teil.id}>
                        <p className="text-[10px] font-black uppercase tracking-[0.16em] text-slate-400 mb-1.5 px-1">
                            {block.teil.titel}
                        </p>
                        <ul className="space-y-1">
                            {block.kapitel.map(g => {
                                const b = befunde.get(g.definition.id);
                                const ist = g.definition.id === kennung;
                                return (
                                    <li key={g.definition.id}>
                                        <button
                                            onClick={() => setOffen(g.definition.id)}
                                            className={`w-full text-left px-3 py-2 rounded-lg border text-sm flex items-center gap-2 transition-colors ${
                                                ist
                                                    ? 'bg-fuchsia-50 border-fuchsia-300 text-fuchsia-900'
                                                    : 'bg-white border-slate-200 hover:border-slate-300 text-slate-700'
                                            }`}
                                        >
                                            <span className="text-[10px] font-black text-slate-300 tabular-nums w-10 shrink-0">
                                                {g.nummer}
                                            </span>
                                            <span className="flex-1 min-w-0 truncate">{g.definition.titel}</span>
                                            {b?.ausgeschlossen
                                                ? <XCircle className="w-3.5 h-3.5 text-slate-300 shrink-0" />
                                                : b?.vollstaendig
                                                    ? <Check className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                                                    : g.pflicht
                                                        ? <span className="w-1.5 h-1.5 rounded-full bg-amber-400 shrink-0" />
                                                        : null}
                                        </button>
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                ))}
            </div>

            {/* ─── Das gewählte Kapitel ─────────────────────────────────── */}
            <div className="space-y-4 min-w-0">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                        <h3 className="text-base font-bold text-slate-800">
                            {aktiv.nummer && <span className="text-slate-300 mr-2">{aktiv.nummer}</span>}
                            {aktiv.definition.titel}
                        </h3>
                        <p className="text-[11px] text-slate-400 mt-0.5">
                            {aktiv.definition.id} · {aktiv.pflicht ? 'Pflichtkapitel' : 'bei Bedarf'}
                            {aktiv.eigen && ' · eigenes Kapitel'}
                        </p>
                    </div>
                    {!gesperrt && !aktiv.pflicht && (
                        <button
                            onClick={() => schreiben(() => service.setzeAbschnitt(
                                ordnung, kennung, { gezogen: !(abschnitt?.gezogen ?? true) }, ctx,
                            ))}
                            className="text-[12px] font-semibold text-slate-500 hover:text-slate-800 border border-slate-200 rounded-lg px-3 py-1.5"
                        >
                            {abschnitt && !abschnitt.gezogen ? 'Kapitel wieder aufnehmen' : 'Trifft nicht zu'}
                        </button>
                    )}
                </div>

                {abschnitt && !abschnitt.gezogen ? (
                    <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-sm text-slate-600">
                        <p className="font-semibold text-slate-700">Geprüft, trifft nicht zu.</p>
                        <p className="mt-1 text-[12px]">
                            Das Kapitel wird im Dokument mit genau diesem Vermerk gedruckt — nicht weggelassen.
                            Ein fehlendes Kapitel sagt etwas anderes als ein ausdrücklich geprüftes.
                        </p>
                    </div>
                ) : (
                    <>
                        <div>
                            <label className="text-[11px] font-black uppercase tracking-[0.14em] text-slate-400">
                                Vorspann des Kapitels
                            </label>
                            <textarea
                                value={einleitung ?? abschnitt?.einleitung ?? ''}
                                onChange={e => setEinleitung(e.target.value)}
                                onBlur={speichereEinleitung}
                                disabled={gesperrt}
                                rows={2}
                                placeholder="Optionaler Einleitungssatz, der vor den Regelungen steht."
                                className="mt-1 w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:border-fuchsia-400 focus:outline-none disabled:bg-slate-50"
                            />
                        </div>

                        <div className="space-y-2">
                            <div className="flex items-center justify-between">
                                <p className="text-[11px] font-black uppercase tracking-[0.14em] text-slate-400">
                                    Regelungen ({regeln.length})
                                </p>
                                {!gesperrt && (
                                    <button
                                        onClick={neueRegel}
                                        className="text-[12px] font-bold text-fuchsia-600 hover:text-fuchsia-700 flex items-center gap-1"
                                    >
                                        <Plus className="w-3.5 h-3.5" /> Regelung hinzufügen
                                    </button>
                                )}
                            </div>

                            {!regeln.length && (
                                <p className="text-[12px] text-slate-400 italic border border-dashed border-slate-200 rounded-lg px-3 py-4 text-center">
                                    Noch keine Regelung. Was hier fehlt, erscheint im Dokument als ausdrücklicher
                                    Leerbefund — nicht als Lücke, die niemand sieht.
                                </p>
                            )}

                            {regeln.map((r, i) => (
                                <div
                                    key={r.id}
                                    className={`border rounded-xl p-3 ${
                                        r.bestaetigt_am ? 'bg-white border-emerald-200' : 'bg-slate-50/60 border-slate-200'
                                    }`}
                                >
                                    <div className="flex items-start gap-2">
                                        <span className="text-[11px] font-black text-slate-300 tabular-nums mt-2 w-5 shrink-0">
                                            {i + 1}
                                        </span>
                                        <textarea
                                            value={entwurf[r.id] ?? r.wortlaut}
                                            onChange={e => setEntwurf(v => ({ ...v, [r.id]: e.target.value }))}
                                            onBlur={() => speichereRegel(r)}
                                            disabled={gesperrt}
                                            rows={2}
                                            placeholder="Wortlaut der Regelung — so, wie sie im Dokument steht."
                                            className="flex-1 text-sm border border-slate-200 rounded-lg px-3 py-2 focus:border-fuchsia-400 focus:outline-none disabled:bg-slate-50"
                                        />
                                    </div>
                                    <div className="flex items-center gap-3 mt-2 pl-7 flex-wrap">
                                        <input
                                            value={r.verantwortlich}
                                            onChange={e => schreiben(() => service.updateRegel(r.id, { verantwortlich: e.target.value }, ctx))}
                                            disabled={gesperrt}
                                            placeholder="Verantwortlich"
                                            className="text-[12px] border border-slate-200 rounded-md px-2 py-1 w-40 disabled:bg-slate-50"
                                        />
                                        <input
                                            value={r.rhythmus}
                                            onChange={e => schreiben(() => service.updateRegel(r.id, { rhythmus: e.target.value }, ctx))}
                                            disabled={gesperrt}
                                            placeholder="Rhythmus"
                                            className="text-[12px] border border-slate-200 rounded-md px-2 py-1 w-32 disabled:bg-slate-50"
                                        />
                                        <div className="flex-1" />
                                        {!gesperrt && (
                                            <>
                                                <button
                                                    onClick={() => schreiben(() => service.bestaetigeRegel(r.id, !r.bestaetigt_am, ctx))}
                                                    className={`text-[12px] font-bold rounded-md px-2.5 py-1 flex items-center gap-1 ${
                                                        r.bestaetigt_am
                                                            ? 'bg-emerald-50 text-emerald-700'
                                                            : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                                                    }`}
                                                    title="Erst eine bestätigte Regelung geht ins Dokument."
                                                >
                                                    <Check className="w-3.5 h-3.5" />
                                                    {r.bestaetigt_am ? 'bestätigt' : 'bestätigen'}
                                                </button>
                                                {/* Mit Abwahl-Vermerk (Plan F15): sonst legte die
                                                    Automatik einen geloeschten Katalogbaustein
                                                    beim naechsten Oeffnen wieder an. Die offenen
                                                    Punkte der Regel werden dabei erledigt — sonst
                                                    stuenden sie ohne Regel weiter offen. */}
                                                <button
                                                    onClick={() => schreiben(() => service.loescheRegelnMitAbwahl([r], ordnung, ctx))}
                                                    className="text-slate-300 hover:text-rose-500"
                                                    title="Regelung löschen"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </>
                                        )}
                                    </div>
                                    {!r.bestaetigt_am && (
                                        <p className="text-[11px] text-amber-600 mt-1.5 pl-7 flex items-center gap-1">
                                            <ChevronRight className="w-3 h-3" />
                                            Noch nicht bestätigt — steht deshalb nicht im Dokument.
                                        </p>
                                    )}
                                </div>
                            ))}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};
